// "Adjustments recorded against this order" on module 04's S2 (spec 04 §4 S2,
// spec 06 §2.4, §2.12 and §3.9, D-342 ruling 2, D-391 ruling 3). Module 06
// renders its own rows here: the movements and the adjustment requests whose
// `ref_work_order_id` is the order, read through §5 rows 24 and 48 under
// module 06's own read code.
//
// HIDDEN, not empty, for a reader the mirror does not resolve to
// `inventory.read`: both lists would only 403, and an empty section would say
// "nothing was recorded", which that reader cannot know.
//
// A request that posted is listed as the movement it wrote, which carries the
// same reference, so its quantity is not counted twice. The requests that did
// not post (waiting, rejected or withdrawn) are listed with their status and a
// link to the request's own page.
//
// Each list reads its first 50 rows, newest first, and says when there are
// more: an order rarely carries more than a handful of manual corrections, and
// the ledger link shows the rest.

import { enumLabel } from "../../../../lib/enum-label";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Skeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { InventoryStatus, RefLabel } from "./inventory-ui";
import {
  useAdjustmentRequests,
  useMovements,
  useProductIndex,
  type AdjustmentRequest,
  type StockMovement,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { readHeldPayload } from "../lib/adjust";
import { formatTimestamp, formatMeasureWithUom } from "../lib/measure";

/** How many rows each list reads; more is said in words. */
export const WO_ADJUSTMENTS_PAGE = 50;

/** The requests listed beside the movements: every one that did not post. */
export function unpostedRequests(items: readonly AdjustmentRequest[]): AdjustmentRequest[] {
  return items.filter((r) => r.status !== "posted");
}

export function WorkOrderAdjustments({ workOrderId }: { workOrderId: string }) {
  const perms = useInventoryPermissions();
  if (!perms.has("inventory.read")) return null;
  return <Section workOrderId={workOrderId} />;
}

function Section({ workOrderId }: { workOrderId: string }) {
  const { t, i18n } = useTranslation();
  const movements = useMovements({ ref_work_order_id: workOrderId, limit: WO_ADJUSTMENTS_PAGE });
  const requests = useAdjustmentRequests({
    ref_work_order_id: workOrderId,
    limit: WO_ADJUSTMENTS_PAGE,
  });
  const moved = movements.data?.items ?? [];
  const held = unpostedRequests(requests.data?.items ?? []);
  const productIds = [
    ...moved.flatMap((m) => (m.product_id === null ? [] : [m.product_id])),
    ...held.flatMap((r) => {
      const p = readHeldPayload(r.movement_payload)?.productId;
      return p === undefined ? [] : [p];
    }),
  ];
  const products = useProductIndex(productIds);

  const title = t("inventory.woAdjustments.title");
  const failed = [movements.error, requests.error].find(
    (e) => e !== null && !(e instanceof UnauthorizedError),
  );
  let body;
  if (failed !== undefined) {
    const requestId = requestIdOf(failed);
    body = (
      <div className="wo-adj-error" role="alert" data-testid="wo-adjustments-error">
        <p>{t("inventory.woAdjustments.error")}</p>
        {requestId !== undefined ? (
          <p className="wo-adj-muted">
            {t("inventory.woAdjustments.requestId")} <code>{requestId}</code>
          </p>
        ) : null}
        <Button
          size="sm"
          onClick={() => {
            void movements.refetch();
            void requests.refetch();
          }}
        >
          {t("inventory.woAdjustments.retry")}
        </Button>
      </div>
    );
  } else if (movements.isPending || requests.isPending) {
    body = (
      <div role="status" aria-label={t("inventory.woAdjustments.loading")}>
        <Skeleton height="1rem" />
        <Skeleton height="1rem" />
        <Skeleton width="60%" height="1rem" />
      </div>
    );
  } else if (moved.length === 0 && held.length === 0) {
    body = (
      <p className="wo-adj-muted" data-testid="wo-adjustments-empty">
        {t("inventory.woAdjustments.empty")}
      </p>
    );
  } else {
    body = (
      <ul className="wo-adj-list">
        {moved.map((m) => (
          <MovementRow key={m.id} movement={m} sku={skuOf(products.byId, m.product_id)} />
        ))}
        {held.map((r) => (
          <RequestRow
            key={r.id}
            request={r}
            sku={skuOf(products.byId, readHeldPayload(r.movement_payload)?.productId ?? null)}
            locale={i18n.language}
          />
        ))}
        {(movements.data?.next_cursor ?? null) !== null ||
        (requests.data?.next_cursor ?? null) !== null ? (
          <li className="wo-adj-muted" data-testid="wo-adjustments-more">
            {t("inventory.woAdjustments.more", { count: WO_ADJUSTMENTS_PAGE })}
          </li>
        ) : null}
      </ul>
    );
  }
  return (
    <Card title={title} className="wo-adj" data-testid="wo-adjustments">
      {body}
    </Card>
  );
}

function skuOf(
  byId: ReadonlyMap<string, { sku?: string | undefined }>,
  id: string | null,
): { id: string | null; sku: string | undefined } {
  return { id, sku: id === null ? undefined : byId.get(id)?.sku };
}

function MovementRow({
  movement,
  sku,
}: {
  movement: StockMovement;
  sku: { id: string | null; sku: string | undefined };
}) {
  const { t, i18n } = useTranslation();
  return (
    <li
      className="wo-adj-row"
      data-testid="wo-adjustment-movement"
      data-movement-id={movement.id}
      data-type={movement.movement_type}
    >
      <span className="wo-adj-when">{formatTimestamp(movement.moved_at, i18n.language)}</span>
      <span className="wo-adj-what">
        {enumLabel(t, "inventory.vocab.movement", movement.movement_type)}
        {" · "}
        <RefLabel value={sku.sku} id={sku.id} />
      </span>
      <span className="num wo-adj-qty">
        {formatMeasureWithUom(movement.quantity, movement.uom, { locale: i18n.language })}
      </span>
      <Link
        to="/inventory/movements"
        search={{
          ...(movement.product_id !== null ? { product_id: movement.product_id } : {}),
          movement_type: movement.movement_type,
        }}
        className="ds-lnk"
      >
        {t("inventory.woAdjustments.inLedger")}
      </Link>
    </li>
  );
}

function RequestRow({
  request,
  sku,
  locale,
}: {
  request: AdjustmentRequest;
  sku: { id: string | null; sku: string | undefined };
  locale: string;
}) {
  const { t } = useTranslation();
  const held = readHeldPayload(request.movement_payload);
  return (
    <li
      className="wo-adj-row"
      data-testid="wo-adjustment-request"
      data-request-id={request.id}
      data-status={request.status}
    >
      <span className="wo-adj-when">{formatTimestamp(request.requested_at, locale)}</span>
      <span className="wo-adj-what">
        <InventoryStatus status={request.status} scope="adjustmentRequest" />{" "}
        <RefLabel value={sku.sku} id={sku.id} />
      </span>
      <span className="num wo-adj-qty">
        {held?.quantity !== undefined
          ? formatMeasureWithUom(held.quantity, held.uom, { locale }).trim()
          : ""}
      </span>
      <Link to="/inventory/approvals/$id" params={{ id: request.id }} className="ds-lnk">
        {t("inventory.woAdjustments.openRequest")}
      </Link>
    </li>
  );
}
